import { inject, observer } from 'mobx-react';
import React from 'react';
import { AnalyticsTrigger, ArrowList, ArrowListItem, Separator } from 'telia-front-react';
import { v4 } from 'uuid';
import Config from '../../Config';
import RouteStore from '../../stores/RouteStore';
import SearchStore, { SELFSERVICE_CATEGORY } from '../../stores/SearchStore';
import SsoStore from '../../stores/SsoStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import { FetchStatus } from '../../util/FetchStatus';
import SearchResultsContainer from '../results/SearchResultsContainer';
import SearchErrorNotice from '../SearchErrorNotice';
import { SearchEvent, SelfServiceSearchGroup } from '../SearchInterfaces';
import SearchResultRow from '../row/SearchResultRow';
import ShowMoreButton from '../ShowMoreButton';

interface Props {
  searchStore?: SearchStore;
  ssoStore?: SsoStore;
  routeStore?: RouteStore;
}

@observer
class SelfServiceSearchResults extends React.Component<Props> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public render() {
    const translate = this.context.translate;
    const {
      searchStore: { searchWrappers },
    } = this.props;

    if (searchWrappers[SELFSERVICE_CATEGORY].status === FetchStatus.UNINITIALIZED) {
      return null;
    }

    return (
      <SearchResultsContainer>
        <Separator spacing="xsmall" weight="thin" />
        <ArrowList type="icon" large={true} backgroundColor="white">
          {searchWrappers[SELFSERVICE_CATEGORY].results.map((result: SelfServiceSearchGroup) => (
            <AnalyticsTrigger trigger={{ id: `Tab - ${SELFSERVICE_CATEGORY} - Link - ${result.title}`, callback: 'onClick' }} key={result.title}>
              <ArrowListItem
                url={`${this.resolveUrl(result)}`}
                icon={`#${result.icon}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => this.handleItemClick(e, result)}
                key={result.title}
              >
                <SearchResultRow>
                  <h3>{result.title}</h3>
                  {result.description}
                </SearchResultRow>
              </ArrowListItem>
              <Separator spacing="xxsmall" weight="thin" fullWidth={true} />
            </AnalyticsTrigger>
          ))}
        </ArrowList>
        <SearchErrorNotice showNoticeWhen={['ERROR', 'FETCH_MORE_ERROR']} status={searchWrappers[SELFSERVICE_CATEGORY].status} translate={translate} />
        <ShowMoreButton category={SELFSERVICE_CATEGORY} />
      </SearchResultsContainer>
    );
  }

  private resolveUrl = (item: SelfServiceSearchGroup) => {
    switch (item.app) {
      case 'selfservice':
        return `${Config.teliaWebSelfServiceUrl}${item.url}`;
      case 'shop':
        return `${Config.teliaShopUrl}${item.url}`;
      default:
        return `${Config.teliaWebUrl}${item.url}`;
    }
  };

  private handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>, item: SelfServiceSearchGroup) => {
    const { routeStore, ssoStore } = this.props;
    e.preventDefault();
    this.sendAlgoliaAnalyticsData(item);
    ssoStore.ensureLogin(undefined, () => routeStore.push(this.resolveUrl(item)));
  };

  private sendAlgoliaAnalyticsData = async (result: SelfServiceSearchGroup) => {
    const { searchStore } = this.props;
    const idxName = `${Config.componentTier}_selfservice`;
    const searchEvent: SearchEvent = {
      eventName: 'SelfService search result click',
      eventType: 'click',
      index: idxName,
      objectIDs: [result.objectID],
      userToken: v4(),
      queryID: searchStore.searchWrappers[SELFSERVICE_CATEGORY].queryId,
      positions: [searchStore.searchWrappers[SELFSERVICE_CATEGORY].results.indexOf(result) + 1],
    };

    searchStore.postSearchTabAnalytics(SELFSERVICE_CATEGORY);
    searchStore.postSearchResultClickEvent(searchEvent);
  };
}

export default inject('searchStore', 'ssoStore', 'routeStore')(SelfServiceSearchResults);
